// Task modal — handles both add (no id yet) and edit (existing id) modes.

import type { Quadrant } from "../generated/Quadrant";
import { local, localApply, sendEditing, tasksInQuadrant } from "../state";
import { generateKeyBetween } from "../shared/ranking";
import { generateTaskId } from "../shared/ids";
import { todayLocalIsoDate } from "../shared/today";
import { keepOverlayAboveKeyboard } from "./modal-viewport";
import { isTauri } from "../config/backend";

/** WebKitGTK (the Linux desktop webview) leaves the native `<input type="date">`
 *  calendar popover open after a date is chosen — it only dismisses when the
 *  whole window loses focus, which traps the modal (clicking other fields does
 *  nothing). Blurring the input once a value is committed closes the popover.
 *  Gated to the apps; web pickers already close on selection. */
function dismissNativeDatePopover(input: HTMLInputElement): void {
  if (!isTauri()) return;
  input.addEventListener("change", () => input.blur());
}

type OpenArgs =
  | { mode: "add"; quadrant: Quadrant }
  | { mode: "edit"; taskId: string };

export function openTaskModal(args: OpenArgs): void {
  closeModal();

  const overlay = document.createElement("div");
  overlay.className = "modal__overlay";
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) closeModal();
  });

  const dialog = document.createElement("div");
  dialog.className = "modal";
  dialog.setAttribute("role", "dialog");
  dialog.setAttribute("aria-modal", "true");

  let initialLabel = "";
  let initialDate: string | null = null;
  let initialCompletedAt: string | null = null;
  let targetQuadrant: Quadrant;

  if (args.mode === "edit") {
    const t = local.state.tasks[args.taskId];
    if (!t) {
      closeModal();
      return;
    }
    initialLabel = t.label;
    initialDate = t.dueDate;
    initialCompletedAt = t.completedAt ?? null;
    targetQuadrant = t.quadrant;
  } else {
    targetQuadrant = args.quadrant;
  }

  const title = document.createElement("h2");
  title.className = "modal__title";
  title.textContent =
    args.mode === "add"
      ? `New task — ${labelFor(targetQuadrant)}`
      : "Edit task";

  const form = document.createElement("form");
  form.className = "modal__form";

  const labelInput = document.createElement("input");
  labelInput.type = "text";
  labelInput.maxLength = 200;
  labelInput.required = true;
  labelInput.placeholder = "What needs doing?";
  labelInput.className = "modal__label-input";
  labelInput.value = initialLabel;

  const dateInput = document.createElement("input");
  dateInput.type = "date";
  dateInput.className = "modal__date-input";
  if (initialDate) dateInput.value = initialDate;
  dismissNativeDatePopover(dateInput);

  const labelRow = field("Label", labelInput);
  const dateRow = field("Due date (optional)", dateInput);

  // "Completed" controls (edit mode only): a checkbox + date input that
  // are linked — checking the box defaults the date to today, unchecking
  // clears it.
  const completedCheckbox = document.createElement("input");
  completedCheckbox.type = "checkbox";
  completedCheckbox.className = "modal__completed-check";
  completedCheckbox.checked = initialCompletedAt !== null;

  const completedDate = document.createElement("input");
  completedDate.type = "date";
  completedDate.className = "modal__date-input";
  completedDate.value = initialCompletedAt ?? "";
  completedDate.disabled = !completedCheckbox.checked;
  dismissNativeDatePopover(completedDate);

  completedCheckbox.addEventListener("change", () => {
    if (completedCheckbox.checked) {
      completedDate.disabled = false;
      if (!completedDate.value) completedDate.value = todayLocalIsoDate();
    } else {
      completedDate.disabled = true;
      completedDate.value = "";
    }
  });

  const completedRow = completedField(
    "Completed",
    completedCheckbox,
    completedDate,
  );

  const error = document.createElement("p");
  error.className = "modal__error";
  error.hidden = true;

  const actions = document.createElement("div");
  actions.className = "modal__actions";

  const save = () => {
    const label = labelInput.value.trim();
    if (!label || label.length > 200) {
      showError(error, "Label must be 1–200 characters.");
      labelInput.focus();
      return;
    }
    const due = dateInput.value || null;
    if (args.mode === "add") {
      const siblings = tasksInQuadrant(targetQuadrant);
      const lastRank =
        siblings.length > 0 ? siblings[siblings.length - 1]![1].rank : null;
      const rank = generateKeyBetween(lastRank, null);
      localApply({
        type: "add",
        task: {
          id: generateTaskId(),
          label,
          dueDate: due,
          quadrant: targetQuadrant,
          rank,
        },
      });
    } else {
      const wantsCompleted = completedCheckbox.checked;
      let nextCompletedAt: string | null = null;
      if (wantsCompleted) {
        nextCompletedAt = completedDate.value || todayLocalIsoDate();
      }
      // Only include `completedAt` in the patch when it actually changed,
      // so we don't broadcast no-op updates.
      const completedChanged = nextCompletedAt !== initialCompletedAt;
      localApply({
        type: "update",
        id: args.taskId,
        patch: {
          label,
          dueDate: due,
          ...(completedChanged ? { completedAt: nextCompletedAt } : {}),
        },
      });
    }
    closeModal();
  };

  // The submit handler covers both Enter-in-an-input and primary-button
  // click. preventDefault keeps the browser from actually submitting
  // the (detached, post-close) form.
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    save();
  });

  const primary = document.createElement("button");
  primary.type = "submit";
  primary.className = "btn btn--primary";
  primary.textContent = args.mode === "add" ? "Add" : "Save";

  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "btn";
  cancel.textContent = "Cancel";
  cancel.addEventListener("click", () => closeModal());

  actions.append(cancel, primary);

  if (args.mode === "edit") {
    const del = document.createElement("button");
    del.type = "button";
    del.className = "btn btn--danger";
    del.textContent = "Delete";
    del.addEventListener("click", () => {
      if (!confirm("Delete this task?")) return;
      localApply({ type: "delete", id: args.taskId });
      closeModal();
    });
    actions.prepend(del);
  }

  if (args.mode === "edit") {
    form.append(labelRow, dateRow, completedRow, error, actions);
  } else {
    form.append(labelRow, dateRow, error, actions);
  }
  dialog.append(title, form);
  overlay.append(dialog);
  document.body.append(overlay);
  keepOverlayAboveKeyboard(overlay);
  trapFocus(dialog);
  labelInput.focus();
  labelInput.select();

  // Broadcast editing state to peers. Cleared when the modal closes.
  if (args.mode === "edit") sendEditing(args.taskId);
}

export function closeModal(): void {
  let hadModal = false;
  for (const ov of document.querySelectorAll(".modal__overlay")) {
    ov.remove();
    hadModal = true;
  }
  if (hadModal) sendEditing(null);
}

function field(labelText: string, input: HTMLInputElement): HTMLElement {
  const row = document.createElement("label");
  row.className = "modal__row";
  const span = document.createElement("span");
  span.textContent = labelText;
  row.append(span, input);
  return row;
}

/** A `modal__row` variant carrying a checkbox AND a date input — the
 *  three live on a single horizontal row, like "[✓] Completed [date]". */
function completedField(
  labelText: string,
  check: HTMLInputElement,
  date: HTMLInputElement,
): HTMLElement {
  const row = document.createElement("label");
  row.className = "modal__row modal__row--inline modal__row--completed";
  const span = document.createElement("span");
  span.textContent = labelText;
  row.append(check, span, date);
  return row;
}

function showError(el: HTMLElement, msg: string): void {
  el.textContent = msg;
  el.hidden = false;
}

function trapFocus(dialog: HTMLElement): void {
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeModal();
      window.removeEventListener("keydown", onKey);
    } else if (e.key === "Tab") {
      const focusables = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'input, button, [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };
  window.addEventListener("keydown", onKey);
}

function labelFor(q: Quadrant): string {
  switch (q) {
    case "q1":
      return "Now · Do first";
    case "q2":
      return "Strategic · Schedule";
    case "q3":
      return "Interruptions · Delegate & batch";
    case "q4":
      return "Distractions · Defer or drop";
  }
}
